import React, { FC, useEffect, useRef } from "react"
import { GatsbyImage } from "gatsby-plugin-image"
import { graphql, useStaticQuery } from "gatsby"

import { Badge, BADGE_COLORS } from "@helpscout/hs-marketing-shared-components"

import { isClientSide } from "@utils"

import {
  PostCategoryFragmentFragment,
  PostCategoryFullDataFragmentFragment,
  PostsHeroQueryQuery,
} from "@graphqlTypes"

import AnchorLink from "@components/AnchorLink/AnchorLink"
import Hero from "@components/Hero/Hero"
// import ImageAboveFold from "@components/ImageAboveFold/ImageAboveFold"

import PostsNav from "../PostsNav/PostsNav"

import "./PostsHero.scss"

type PostHeroProps = {
  allCategories?: PostCategoryFragmentFragment[]
  categoryPermalink: PostCategoryFullDataFragmentFragment["permalink"]
  categorySlug: PostCategoryFullDataFragmentFragment["slug"]
  currentTopicSlug?: string
  description?: PostCategoryFullDataFragmentFragment["seoDescription"]
  isLandingPage?: boolean
  title: PostCategoryFullDataFragmentFragment["title"]
}

const PostsHero: FC<PostHeroProps> = ({
  categorySlug,
  currentTopicSlug,
  description,
  isLandingPage,
  title,
}) => {
  const topicsScrollElRef = useRef<HTMLDivElement>(null)

  const { productUpdatesImage } = useStaticQuery<PostsHeroQueryQuery>(graphql`
    query PostsHeroQuery {
      productUpdatesImage: contentfulAsset(
        contentful_id: {eq: "3y11v1JTM6rV58Txs6zYsY"}
      ) {
        imgixImage {
          gatsbyImageData(width: 395)
        }
      }
    }
  `)

  useEffect(() => {
    if (!isClientSide() || !topicsScrollElRef.current || !currentTopicSlug)
      return

    const currentNavEl = topicsScrollElRef.current.querySelector(
      `.${currentTopicSlug}`
    )
    if (!currentNavEl) return

    const positionXToScroll =
      currentNavEl.getBoundingClientRect().x - 15 - window.innerWidth * 0.025

    topicsScrollElRef.current.scroll({ left: positionXToScroll })
  }, [currentTopicSlug])

  return (
    <Hero style={{ overflow: "visible" }}>
      <div
        className={`PostsHero ${isLandingPage ? "is-landing-page" : ""}`}
      >
        <PostsNav currentCategorySlug={categorySlug} />
        {!isLandingPage && (
          <div className="PostsHero--content">
            <div className="container">
              <div className="grid">
                <div className="column column--1-7-L">
                  {/* Don't show the title on the archive page */}
                  <h1>{title}</h1>
                  {description && (
                    <p className="type--text-L posts-hero-description">
                      {description}
                    </p>
                  )}
                </div>
                {categorySlug === "product" && (
                  <div className="column column--1-5-S column--1-7-M column--9-13-L">
                    <AnchorLink
                      className="PostsHero--product-updates-callout"
                      href="https://updates.helpscout.com"
                    >
                      {productUpdatesImage?.imgixImage?.gatsbyImageData && (
                        <GatsbyImage
                          alt=""
                          role="presentation"
                          image={productUpdatesImage.imgixImage.gatsbyImageData}
                        />
                      )}
                      <Badge color={BADGE_COLORS.COBALT}>New</Badge>
                      <h4 className="type--headline-medium-L">Product updates</h4>
                      <p className="type--text-XS">Stay up to date with the latest Help Scout updates.</p>
                    </AnchorLink>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Hero>
  )
}

export default PostsHero
